import { classes } from 'html-classes';
import { noop } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import {
  type MouseEvent,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Barcode from 'react-barcode';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import type { OrderItemBundle } from '~/api/Catalog';
import { ETADeliveryMethodType } from '~/api/ETADeliveryMethodType';
import type {
  Order,
  OrderCartItem,
  OrderCartItemStatus,
  OrderShort,
} from '~/api/Order';
import { company, DEFAULT_COMPANIES } from '~/company/Company';
import { CompanyName } from '~/company/interface';
import Icon from '~/components/Icon/Icon';
import MainSidebar from '~/components/MainSidebar/MainSidebar';
import { ModalType } from '~/components/Modal/interface';
import { PartiallyPicked } from '~/components/PartiallyPicked/PartiallyPicked';
import { RateAppAlert } from '~/components/RateAppAlert';
import { useGlobal } from '~/hooks/useGlobal';
import { useIntercom } from '~/hooks/useIntercom';
import { useReorder } from '~/hooks/useReorder';
import { STATUS_ICONS } from '~/pages/Order/components/OrderStatusBadge/constants';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { mainStore, orderStore, userStore } from '~/stores';
import { Product } from '~/stores/CategoriesStore';
import { favoritesStore } from '~/stores/FavoritesStore/FavoritesStore';
import type { OrderStatusStore } from '~/stores/OrderStore';
import { formatPriceWithCurrency } from '~/utils/formaters';
import { normalizeOrderId } from '~/utils/normalizeOrderId';
import { pluralize } from '~/utils/pluralize';
import { pollingBuilder } from '~/utils/pollingBuilder';

import {
  DELIVERY_INDICATORS,
  DeliveringStatus,
} from './components/DeliveringStatus';
import { OrderBottomSheet } from './components/OrderBottomSheet';
import { OrderDateText } from './components/OrderDateText';
import { PickUpOrderHeader } from './components/PickUpOrderHeader';
import { ShareBlock } from './components/ShareBlock';
import { ZoneDescription } from './components/ZoneDescription/ZoneDescription';
import styles from './Order.module.scss';
import OrderProductCard from './OrderProductCard';
import OrderSkeleton from './OrderSkeleton';

import './styles.scss';
import { Typography } from '@workspace/ui/components/Typography';

const PATH = '/cabinet/orders';

const OrderPage = observer(() => {
  const { t } = useTranslation();
  const { orderId } = useParams<{ orderId: string }>();
  const { state } = useLocation();
  const [order, setOrder] = useState<Order | OrderShort | null>();
  const [, setBrokenOrderType] = useState<
    'missingItems' | 'notDelivered' | null
  >(null);
  const [publicStatus, setPublicStatus] =
    useState<OrderStatusStore>('accepted');
  const [products, setProducts] = useState<Record<string, Product>>({});
  const stopWatchRef = useRef<(() => void) | null>(null);
  const { isTablet, isMobile } = useGlobal();
  const navigate = useNavigate();
  const {
    openMessenger,
    updateIntercom,
    isOpen: isIntercomOpen,
  } = useIntercom();

  const backPath = state && state.tab ? `${PATH}/${state.tab}` : PATH;
  const handleBackClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    navigate(backPath);
  };
  const isPickUpOrder =
    order?.delivery_method === ETADeliveryMethodType.ClickAndCollect;

  const showMapStatus = Boolean(
    order &&
      DELIVERY_INDICATORS.includes(order.public_status) &&
      !isPickUpOrder,
  );

  const shouldHideMap = company.hideOrderMap;
  const showOrderBarcode =
    shouldHideMap &&
    order != null &&
    !isPickUpOrder &&
    !['delivered', 'failed', 'cancelled'].includes(order.public_status);
  const isMapFullscreen =
    showMapStatus && (isTablet || isMobile) && !shouldHideMap;

  useEffect(() => {
    if (order) {
      analyticsService.track(AnalyticsEvent.ORDER_PAGE_VIEW, {
        orderId: order.id,
      });
    }
  }, [order?.id]);

  useEffect(() => {
    if (isIntercomOpen && order?.id) {
      updateIntercom({ order_number: order.order_id });
    }
    return () => {
      updateIntercom({ order_number: null });
    };
  }, [order?.id, isIntercomOpen]);

  const orderStatus = (
    <div className={classes(['header__info-status', order?.public_status])}>
      {order && <Icon type={STATUS_ICONS[order.public_status]} size={20} />}
      {t(publicStatus)}
    </div>
  );
  const [searchParams] = useSearchParams();
  const openedModal = searchParams.get('modal');

  const goBackSection = useMemo(() => {
    if (
      company.name === CompanyName.CityDrinks &&
      isMobile &&
      !isMapFullscreen
    ) {
      return null;
    }

    if (company.variant({ '1': !isMapFullscreen, '2': false })) {
      return (
        <Link
          to={backPath}
          onClick={handleBackClick}
          replace={true}
          className="go-back"
        >
          <Icon type="chevron" className="icon__rotate-90" />
          <span>{t('phrases:backToOrderList')}</span>
        </Link>
      );
    }
    if (isTablet && !showMapStatus) {
      return (
        <Link
          data-company={company.name}
          onClick={handleBackClick}
          to={backPath}
          replace={true}
          className="go-back-portalled w-text fullwidth"
          dir={userStore.dir}
        >
          <Icon
            type="arrow"
            className="icon__rotate-180"
            data-company={company.name}
          />
          {order && (
            <span>
              {t('phrases:orderNumber', {
                number: normalizeOrderId(order?.order_id),
              })}
            </span>
          )}
          {orderStatus}
        </Link>
      );
    }
    return createPortal(
      <Link
        to={backPath}
        onClick={handleBackClick}
        data-company={company.name}
        className="go-back-portalled"
        replace={true}
        dir={userStore.dir}
      >
        <Icon
          type="arrow"
          className="icon__rotate-180 dark"
          data-company={company.name}
        />
      </Link>,
      document.body,
    );
  }, [isTablet, order, isMapFullscreen, isMobile, backPath]);

  const updateStatus = (order: OrderShort) => {
    let status: OrderStatusStore;
    switch (order.public_status) {
      case 'in_delivery':
        status = 'inDelivery';
        break;
      case 'ready_to_ship':
        status = 'readyToShip';
        break;
      case 'ready_for_pickup':
        status = 'readyForPickup';
        break;
      default:
        status = order.public_status;
        break;
    }
    setPublicStatus(status);
  };

  useLayoutEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) {
        return;
      }

      try {
        return await orderStore.requestOrder(orderId);
      } catch (error) {
        console.error(error);
      }
    };

    const fetchProducts = async (
      order: Order | OrderShort,
    ): Promise<Record<string, Product>> => {
      const products: Record<string, Product> = {};

      for (const item of order.items) {
        try {
          const product = await Product.requestById(item.product_id).catch(
            noop,
          );

          if (!product) {
            continue;
          }

          products[product.id] = product;
        } catch (error) {
          console.error(error);
        }
      }

      if (order.bundles) {
        for (const item of order.bundles) {
          try {
            const product = await Product.requestById(item.catalog_bundle_id);

            if (!product) {
              continue;
            }

            products[product.id] = product;
          } catch (error) {
            console.error(error);
          }
        }
      }

      return products;
    };

    const requestData = async () => {
      const order = await fetchOrder();

      setOrder(order);

      if (!order) {
        return;
      }

      updateStatus(order);

      const products = await fetchProducts(order);

      setProducts(products);

      const itemBrokenStatuses: OrderCartItemStatus[] = [
        'missing',
        'picked_partially',
        'out_of_stock',
      ];
      const allItemsMissing = order.items.every(
        (item) => itemBrokenStatuses.indexOf(item.status) !== -1,
      );
      if (allItemsMissing) {
        setBrokenOrderType('notDelivered');
      } else {
        const someItemsMissing = order.items.some(
          (item) => itemBrokenStatuses.indexOf(item.status) !== -1,
        );
        if (someItemsMissing) {
          setBrokenOrderType('missingItems');
        }
      }
    };

    void requestData();
  }, [orderId]);

  const handleFavorite = (productId: string) => {
    const product = products[productId];
    if (!product) {
      return;
    }
    favoritesStore.toggleFavorite(product, 'product_main');
    mainStore.sendToRN('hapticFeedback', {
      count: 1,
      type: 'soft',
    });
  };

  useEffect(() => {
    stopWatchRef.current?.();

    if (!orderId) {
      return;
    }

    stopWatchRef.current = pollingBuilder({
      promiseBuilder: () => orderStore.requestOrder(orderId),
      conditionFn: (response) => {
        if (!response) {
          return false;
        }

        setOrder(response);
        updateStatus(response);

        return response.public_status !== 'delivered';
      },
    });

    return () => {
      stopWatchRef.current?.();
    };
  }, [orderId]);

  const getItemsNum = () => {
    const bundles = order?.bundles?.length ?? 0;
    const items = order?.items.filter((i) => !i.bundle_id).length ?? 0;

    return bundles + items;
  };

  const isFeedbackAlertShown = () => {
    const allowedStatuses = [
      'accepted',
      'picking',
      'ready_to_ship',
      'ready_for_pickup',
      'in_delivery',
    ];
    return (
      mainStore.isRN &&
      !userStore.personalData.isRateApp &&
      allowedStatuses.includes(`${order?.public_status}`)
    );
  };

  const orderNumberText = t('phrases:orderNumber', {
    number: normalizeOrderId(order?.order_id ?? ''),
  });

  const { handleReorder, isReorderLoading } = useReorder(order);

  const buttons = (
    <>
      {order?.receipt_url && (
        <a
          href={order.receipt_url}
          target="_blank"
          rel="noreferrer"
          className="button _bordered _white"
          onClick={() => {
            analyticsService.track(AnalyticsEvent.CHECK_BUTTON_CLICKED, {
              orderId: order.id,
            });
          }}
        >
          <Icon type="receipt" size={isMobile ? 16 : 24} />
          Check
        </a>
      )}
      {company.match({
        circlek: (
          <a
            className="button _bordered _white contact-support-mobile__button"
            href={`tel:${company.config.phoneNumber}`}
          >
            <Icon type="whatsapp" size={isMobile ? 16 : 24} />
            {t('contactSupport')}
          </a>
        ),
        ourkids: (
          <a
            className="button _bordered _white contact-support-mobile__button"
            href={`tel:${company.config.phoneNumber}`}
          >
            <Icon type="whatsapp" size={isMobile ? 16 : 24} />
            {t('contactSupport')}
          </a>
        ),
        citydrinks: (
          <button
            className="button _bordered _white contact-support-mobile__button"
            onClick={(e) => {
              analyticsService.track(
                AnalyticsEvent.CONTACT_SUPPORT_BUTTON_CLICKED,
                { orderId: order?.id },
              );
              e.preventDefault();
              openMessenger();
            }}
          >
            {t('contactSupport')}
          </button>
        ),
        default: company.config.links?.whatsapp && (
          <a
            className="button _bordered _white contact-support-mobile__button"
            href={company.config.links.whatsapp}
            target="_blank"
            rel="noreferrer"
            onClick={() => {
              analyticsService.track(
                AnalyticsEvent.CONTACT_SUPPORT_BUTTON_CLICKED,
                { orderId: order?.id },
              );
            }}
          >
            <Icon type="whatsapp" size={isMobile ? 16 : 24} />
            {t('contactSupport')}
          </a>
        ),
      })}
      <button
        className="button _bordered _white"
        onClick={handleReorder}
        disabled={isReorderLoading}
      >
        <Icon type="reorder" size={isMobile ? 16 : 24} />
        {t('reorder')}
      </button>
    </>
  );

  const isPartiallyPicked = useMemo(() => {
    return order?.items.some(
      (i: OrderCartItem) => i.requested_quantity - i.actual_quantity > 0,
    );
  }, [order]);

  const partiallyPickedMessage = isPartiallyPicked &&
    company.showPartiallyPickedInfo &&
    !['inDelivery', 'readyToShip'].includes(publicStatus) && (
      <PartiallyPicked />
    );

  const paidTotal =
    order &&
    formatPriceWithCurrency(
      mainStore.convertPenceToPounds(order.actual_paid_price_money || 0),
    );

  const baseTotal =
    order &&
    formatPriceWithCurrency(
      order.base_total
        ? mainStore.convertPenceToPounds(
            order.base_total - (order.discount_total ?? 0),
          )
        : 0,
    );

  return (
    <div
      className={classes(['order content', styles.pickUpOrderContent])}
      data-company={company.name}
    >
      <aside className="sidebar">
        <MainSidebar />
      </aside>
      <main className={classes(['container', styles.root])}>
        {!order ? (
          <OrderSkeleton />
        ) : (
          <>
            {isPickUpOrder ? (
              <PickUpOrderHeader order={order} />
            ) : (
              goBackSection
            )}

            <OrderBottomSheet
              enabled={isMapFullscreen && openedModal !== ModalType.RateApp}
              className={classes([
                'order-sheet-content',
                isPickUpOrder && styles.pickUpOrderSheet,
              ])}
              withBackdrop={false}
            >
              {!isPickUpOrder &&
                company.variant({ '1': true, '2': !isMobile }) && (
                  <section className="header skeleton">
                    <Icon
                      type="arrow"
                      size={24}
                      className="icon__rotate-180"
                      onClick={handleBackClick}
                    />
                    <div className="title">
                      {DEFAULT_COMPANIES.includes(company.name) ? (
                        <>
                          {t('orderList')} <Icon type="chevron" size={16} />{' '}
                          <span>{orderNumberText}</span>
                        </>
                      ) : (
                        orderNumberText
                      )}
                    </div>
                    <Icon type="copy" size={24} />
                    <div className="header__info">
                      <div className="header__info-time">
                        <OrderDateText createdAt={order.created_at} />
                      </div>
                      {orderStatus}
                    </div>
                    {company.variant({
                      '1': isMobile && (
                        <>
                          <div className="header__time">
                            <OrderDateText createdAt={order.created_at} />
                          </div>
                          <div className="header__buttons">{buttons}</div>
                        </>
                      ),
                    })}
                  </section>
                )}

              {showMapStatus && !shouldHideMap && (
                <DeliveringStatus order={order} className="delivery-status" />
              )}

              <div
                className={classes([
                  styles.shareBlockWr,
                  !isPickUpOrder && styles._oder3,
                ])}
              >
                <ShareBlock orderId={order.id} />
              </div>

              {isFeedbackAlertShown() && (
                <div className={styles.rateFeedback}>
                  <RateAppAlert />
                </div>
              )}

              <div
                className={classes([
                  'content-wrapper',
                  isPickUpOrder && styles.pickUpContentWrapper,
                ])}
              >
                <section className="order__details">
                  <div className="order__details-content">
                    <div className="order__details-content-main">
                      <div className="order__details-item">
                        <Icon
                          type="location"
                          size={24}
                          className="order__details-item-icon"
                        />
                        <div className="order__details-item-content">
                          <p className="order__details-item-content-title">
                            {isPickUpOrder
                              ? t('deliveryMethods:pickupFrom')
                              : t('deliverTo')}
                          </p>
                          <p className="order__details-item-content-value">
                            {isPickUpOrder
                              ? orderStore.etaCalculation?.warehouse.address
                              : order.address?.address_1}
                          </p>
                          <ZoneDescription order={order} />
                        </div>
                      </div>
                      <div className="order__details-item">
                        <Icon
                          type="person"
                          size={24}
                          className="order__details-item-icon"
                        />
                        <div className="order__details-item-content">
                          <p className="order__details-item-content-title">
                            {t('receiver')}
                          </p>
                          <p className="order__details-item-content-value">
                            {order.recipient?.full_name},{' '}
                            {order.recipient?.phone}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="order__details-content-payment">
                      <div className="order__details-payment-info">
                        {!company.isDefaultCompany &&
                          (!isMobile || !isPickUpOrder) && (
                            <div className="order__details-payment-info-item _big">
                              <div
                                className={classes([
                                  isPickUpOrder && styles.pickUpOrderPaid,
                                ])}
                              >
                                <p
                                  className={classes([
                                    !isPickUpOrder && 'payment-paid',
                                  ])}
                                >
                                  {t('total')}
                                </p>
                                <p className="payment-method">
                                  {isMobile && <>&nbsp;</>}
                                  {t(`by${order.payment_method.toLowerCase()}`)}
                                </p>
                              </div>
                              <p>{paidTotal}</p>
                            </div>
                          )}
                        <div
                          className={classes([
                            'order__details-payment-info-item _list d-hide',
                            !isPickUpOrder && '_order-1',
                            isPickUpOrder && styles.pickUpOrderValue,
                            isMobile &&
                              company.isDefaultCompany &&
                              'top_padding',
                          ])}
                        >
                          <div className="order__details-payment-info-item">
                            {(!company.isDefaultCompany && !isPickUpOrder) ||
                            !isMobile ? (
                              <>
                                <p>{t('orderValue')}</p>
                                <p>{baseTotal}</p>
                              </>
                            ) : (
                              <>
                                <p>{t('total')}</p>
                                <p>{paidTotal}</p>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="order__details-payment-info-item _list">
                          <div className="order__details-payment-info-item">
                            <p>
                              {order.tax_percent
                                ? t('phrases:vat', {
                                    percent: order.tax_percent,
                                  })
                                : t('phrases:vat_')}
                            </p>
                            <p>
                              {formatPriceWithCurrency(
                                mainStore.convertPenceToPounds(
                                  order.actual_tax_amount || 0,
                                ),
                              )}
                            </p>
                          </div>
                          {!!order.promocode_discount && (
                            <div className="order__details-payment-info-item">
                              <p>{t('phrases:promoCode')}</p>
                              <p>
                                -
                                {formatPriceWithCurrency(
                                  mainStore.convertPenceToPounds(
                                    order.promocode_discount || 0,
                                  ),
                                )}
                              </p>
                            </div>
                          )}
                          {!!order.actual_paid_loyalty_points && (
                            <div className="order__details-payment-info-item">
                              <p>{t('loyaltyPointsDiscount')}</p>
                              <p>
                                -{' '}
                                {formatPriceWithCurrency(
                                  mainStore.convertPenceToPounds(
                                    order.actual_paid_loyalty_points || 0,
                                  ),
                                )}
                              </p>
                            </div>
                          )}
                          {!isPickUpOrder && (
                            <div className="order__details-payment-info-item">
                              <p>{t('deliveryFeeLong')}</p>
                              <p>
                                {formatPriceWithCurrency(
                                  mainStore.convertPenceToPounds(
                                    order.delivery_price || 0,
                                  ),
                                )}
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="order__details-payment-info-item _bold m-hide">
                          <p>{t('total')}</p>
                          <p>{paidTotal}</p>
                        </div>
                        {isMobile &&
                          company.match({
                            citydrinks: null,
                            v3: null,
                            default: (
                              <div className="order__details-buttons">
                                {buttons}
                              </div>
                            ),
                          })}
                      </div>
                    </div>
                    {(!isMobile || shouldHideMap) && (
                      <div className="order__details-buttons">{buttons}</div>
                    )}
                  </div>
                </section>

                <section className="order__items">
                  <div className={styles.orderListItems__info}>
                    <p className="order__items-title">
                      {getItemsNum()}{' '}
                      {pluralize(getItemsNum(), [
                        t('item'),
                        t('items_few'),
                        t('items'),
                      ])}
                    </p>
                    {partiallyPickedMessage}
                  </div>
                  {isMobile && (showMapStatus || showOrderBarcode) && (
                    <div className="header__info mobile-order-header">
                      {t('phrases:orderNumber', {
                        number: normalizeOrderId(order?.order_id),
                      })}
                      {orderStatus}
                      {showOrderBarcode && (
                        <div className="mobile-order-header__barcode">
                          <Typography.Header100Medium className="my-0 text-center">
                            {t('phrases:showBarcodeToEmployee')}
                          </Typography.Header100Medium>
                          <Barcode
                            value={order?.order_id ?? ''}
                            format="CODE128"
                            displayValue={false}
                            width={1.5}
                            height={50}
                          />
                        </div>
                      )}
                    </div>
                  )}
                  <ul className="order__items-list">
                    {order.bundles?.map((item) => (
                      <li
                        key={item.id}
                        className={classes([
                          (item as OrderItemBundle).is_picked_partialy &&
                            'partiallyDelivered__anchor',
                        ])}
                      >
                        <OrderProductCard
                          item={item}
                          handleFavorite={handleFavorite}
                          isBundle
                          product={products[item.catalog_bundle_id]}
                        />
                      </li>
                    ))}
                    {order.items.reduce((acc: ReactNode[], item) => {
                      !item.bundle_id &&
                        acc.push(
                          <li
                            key={item.id}
                            className={classes([
                              (item as OrderCartItem).requested_quantity >
                                (item as OrderCartItem).actual_quantity &&
                                'partiallyDelivered__anchor',
                            ])}
                          >
                            <OrderProductCard
                              item={item}
                              handleFavorite={handleFavorite}
                              product={products[item.product_id]}
                            />
                          </li>,
                        );
                      return acc;
                    }, [])}
                  </ul>
                </section>
              </div>
              {/* <div className="content-layout contact-support-mobile">
                {company.match({
                  circlek: (
                    <a
                      className="button _bordered contact-support-mobile__button"
                      href={`tel:${company.config.phoneNumber}`}
                    >
                      {t('contactSupport')}
                    </a>
                  ),
                  default: company.config.links?.whatsapp && (
                    <a
                      className="button _bordered contact-support-mobile__button"
                      href={company.config.links.whatsapp}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t('contactSupport')}
                    </a>
                  ),
                })}
              </div> */}
            </OrderBottomSheet>
          </>
        )}
      </main>
    </div>
  );
});

export default OrderPage;
